<template>
  <div>
    <section v-if="!loading">
      <transition name="fade">
        <invite-team-member-popup
          v-if="mode === 'clavo-not-in-integration' || mode === 'integration-not-in-clavo' || mode === 'connect-clavo-to-integration'"
          class="invite-popup"
          :team="team"
          :mode="mode"
          :system="lastSystem"
          :members="members"
          :member-from="memberFrom"
          :matched-users-count="matchedUsersCount"
          :show-illustration="showIllustration"
          @continue="continueIntegration"
          @connect="(memberFrom) => connectClicked(memberFrom.member)"
          @disconnected="(user) => disconnectedUser(user)"
          @close="membersPopupClosed(lastSystem)"
        />
      </transition>
      <transition name="fade">
        <delete-integration-popup
          v-if="cancelIntegrationPopupVisible"
          :mode="mode"
          :team="team"
          :system="lastSystem"
          @close="cancelIntegrationPopupVisible = false"
          @delete-integration="integrationDelete"
        />
      </transition>
      <team-integrations-settings-entry
        :system="'slack'"
        :icon="require('@/assets/_desktop/_settings/_icons/Slack.svg')"
        :description="integrationSubtitles['slack']"
        :settings-title="$t('integrationSettings', { system: 'Slack' })"
        :active-members-count="activeMembersCount"
        :features-list="[
          $t('integrationDescription1'),
          $t('integrationDescription2'),
          $t('integrationDescription3'),
          $t('slackDescription1'),
          $t('slackDescription2'),
        ]"
        :team="team"
        :integrations="integrations"
        :warning="isWarningStatus('slack')"
        @connect-clicked="connectIntegrationClicked"
        @connect-users-clicked="getUsersToManualConnect"
        @disconnect-clicked="disconnectIntegrationClicked"
      ></team-integrations-settings-entry>
      <team-integrations-settings-entry
        v-if="false"
        :system="'teams'"
        :icon="require('@/assets/_desktop/_settings/_icons/MSTeams.svg')"
        :description="integrationSubtitles['teams']"
        :settings-title="$t('integrationSettings', { system: 'Microsoft Teams' })"
        :active-members-count="activeMembersCount"
        :features-list="[$t('integrationDescription1'), $t('integrationDescription2'), $t('integrationDescription3')]"
        :team="team"
        :integrations="integrations"
        :warning="isWarningStatus('teams')"
        @connect-clicked="connectIntegrationClicked"
        @connect-users-clicked="getUsersToManualConnect"
        @disconnect-clicked="disconnectIntegrationClicked"
      ></team-integrations-settings-entry>
      <team-integrations-settings-entry
        :system="'discord'"
        :icon="require('@/assets/_desktop/_settings/_icons/Discord.svg')"
        :description="integrationSubtitles['discord']"
        :settings-title="$t('integrationSettings', { system: 'Discord' })"
        :active-members-count="activeMembersCount"
        :features-list="[$t('integrationDescription1'), $t('integrationDescription2'), $t('integrationDescription3')]"
        :team="team"
        :integrations="integrations"
        :warning="isWarningStatus('discord')"
        @connect-clicked="connectIntegrationClicked"
        @connect-users-clicked="getUsersToManualConnect"
        @disconnect-clicked="disconnectIntegrationClicked"
      ></team-integrations-settings-entry>
    </section>
  </div>
</template>

<script>
const InviteTeamMemberPopup = () => import('@/components/Team/InviteTeamMemberPopup')
import Constants from '@/components/Constants'
import Validation from '@/components/Validation'
import DeleteIntegrationPopup from '@/components/Settings/DeleteIntegrationPopup'
import TeamIntegrationsSettingsEntry from '@/components/Settings/Tabs/TeamIntegrationsSettingsEntry'
import ParseFunctions from '@/components/ParseFunctions'
import EventBus from '@/EventBus'
const { systemFullName } = require('../../../../clavo-shared/constants/index')

export default {
  components: {
    DeleteIntegrationPopup,
    InviteTeamMemberPopup,
    TeamIntegrationsSettingsEntry,
  },
  mixins: [Constants, Validation, ParseFunctions],
  props: {
    team: { type: Object, default: undefined },
  },
  data() {
    return {
      loading: true,
      integrations: {},
      integrationSubtitles: {
        slack: this.$t('slackIntegrationDescription'),
        teams: this.$t('teamsIntegrationDescription'),
        discord: this.$t('discordIntegrationDescription'),
      },
      slackSettings: null,
      cancelIntegrationPopupVisible: false,
      isRightAfterInstallation: false,
      lastStatus: undefined,
      lastSystem: undefined,
      members: [],
      activeMembersCount: undefined,
      matchedUsersCount: null,
      mode: undefined,
      memberFrom: undefined,
      isFirstLoad: true,
      showIllustration: false,
    }
  },
  // computed: {},
  watch: {
    integrations() {},
  },
  async created() {
    let state = this.$route.query.state

    if (state) {
      let system = this.$route.query.system
      let code = this.$route.query.code
      let state = this.$route.query.state
      let guild_id = this.$route.query.guild_id
      this.$store.commit('setShowProgressBar', true)
      let res = await this.verifyIntegrationStateAndApproveInstall(code, state, system, guild_id)

      if (res.status === 'ok') {
        this.isRightAfterInstallation = true
        this.lastSystem = system
        this.members = []
        this.mode = 'clavo-not-in-integration'
      } else {
        this.showServerError(res)
      }
      this.$store.commit('setShowProgressBar', false)
    }
    await this.load()
  },
  mounted() {
    EventBus.$on('back', this.handleBackButton)
  },
  beforeDestroy() {
    EventBus.$off('back', this.handleBackButton)
  },
  methods: {
    async load() {
      this.isFirstLoad = true
      await this.loadIntegrations()
    },
    async loadIntegrations() {
      this.loading = true
      let res = await this.getIntegrations(this.team.teamId)
      if (res.status === 'ok') {
        this.integrations = res.integrations
        this.activeMembersCount = res.activeMembersCount
        if (this.integrations && this.lastSystem) {
          let status = this.getSystemStatus(this.lastSystem)
          this.runActionBasedOnStatus(status, this.lastSystem)
        }
        this.setAllIntegrationsDescriptions()
      } else {
        this.showServerError(res)
      }
      this.loading = false
    },
    setAllIntegrationsDescriptions() {
      for (const [system, integration] of Object.entries(this.integrations)) {
        let status = integration._status
        this.setSystemDescription(status, system)
      }
    },
    async connectIntegrationClicked(system) {
      let res = await this.getIntegrationInstallUrl(this.team.teamId, 'teamIntegrationsSettings', system)
      if (res.status === 'ok') {
        let state = res.state
        window.open(res.url, '_self')
      } else {
        this.showServerError(res)
      }
    },
    disconnectIntegrationClicked(system) {
      this.lastSystem = system
      this.cancelIntegrationPopupVisible = true
    },
    async integrationDelete(params) {
      let system = params.system
      let res = await this.removeIntegration(this.team.teamId, system)

      if (res.status === 'ok') {
        if (system === 'slack') {
          this.integrationSubtitles[system] = this.$t('slackIntegrationDescription')
        }

        await this.load()
      } else {
        this.showServerError(res)
      }
    },
    async runActionBasedOnStatus(status, system) {
      this.$store.commit('setShowProgressBar', true)
      this.setSystemDescription(status, system)

      if (status === 'authorized' && this.mode === 'clavo-not-in-integration') {
        await this.getUsersToManualConnect(system)
      }
      this.$store.commit('setShowProgressBar', false)
    },
    setSystemDescription(status, system) {
      if (status === 'authorized') {
        if (this.isRightAfterInstallation) {
          this.integrationSubtitles[system] = this.$t('integrationDescAuthorized', { system: systemFullName(system) })
        } else {
          this.integrationSubtitles[system] = this.$t('integrationDescAuthorizedManual', { system: systemFullName(system) })
        }
      } else if (status === 'primaryOwnerNotFound') {
        this.integrationSubtitles[system] = this.$t('integrationNeedPrimaryOwner', { system: systemFullName(system) })
      } else if (status === '') {
      }
    },
    isWarningStatus(system) {
      let status = this.getSystemStatus(system)
      return status && status !== 'authorized'
    },
    async getUsersToManualConnect(system) {
      this.$store.commit('setShowProgressBar', true)
      this.lastSystem = system

      this.integrationSubtitles[system] = this.$t('integrationUsersRetrieving', { system: systemFullName(system) })
      let res
      let useCache = !this.isFirstLoad
      this.members = []
      res = await this.getUsersNotInIntegration(this.team.teamId, system, useCache)

      if (res.status === 'ok') {
        let members = res.teamMembers
        this.matchedUsersCount = res.matchedUsersCount
        this.mode = 'clavo-not-in-integration'
        if (members.length > 0) {
          this.members = members
        } else {
          this.showIllustration = true
        }
      } else {
        this.showServerError(res)
      }

      this.$store.commit('setShowProgressBar', false)
    },
    getURL(team) {
      return team.logo ? team.logo._url : null
    },
    async uploadLogoPreview(e) {
      this.imageFile = e
      var file = e.target.files[0]
      this.imageSrc = URL.createObjectURL(file)
    },
    getSystemStatus(system) {
      if (this.integrations) {
        let systemData = this.integrations[system] ? true : false
        if (systemData) {
          return this.integrations[system]._status
        }
        return undefined
      }
    },
    membersPopupClosed(system) {
      if (this.isRightAfterInstallation) {
        this.isRightAfterInstallation = false
        this.integrationSubtitles[system] = this.$t('integrationDescAuthorized', { system })
      }
      this.mode = undefined
      this.isFirstLoad = true
      this.setSystemDescription(this.getSystemStatus(system), system)
    },
    async getMembersForIntegration() {
      if (this.mode === 'clavo-not-in-integration') {
        this.members = []
        let res = await this.getIntegrationUnmatchedUsers(this.team.teamId, this.lastSystem)
        if (res.status === 'ok') {
          this.members = res.teamMembers
          if (this.members.length === 0) this.showIllustration = true
          // this.showIntegrationMembersToAdd = true
        } else {
          this.showServerError(res)
        }
        this.mode = 'integration-not-in-clavo'
      } else if (this.mode === 'integration-not-in-clavo') {
        this.mode = undefined
      }
    },
    async continueIntegration() {
      this.showIllustration = false
      this.getMembersForIntegration()
    },
    async connectClicked(memberFrom) {
      this.memberFrom = memberFrom
      this.mode = 'connect-clavo-to-integration'
      this.members = []
      let res = await this.getIntegrationUnmatchedUsers(this.team.teamId, this.lastSystem)
      if (res.status === 'ok') {
        this.members = res.teamMembers
      } else {
        this.showServerError(res)
      }
    },

    disconnectedUser(user) {
      if (this.integrations) {
        let status = this.getSystemStatus(this.lastSystem)
        this.runActionBasedOnStatus(status, this.lastSystem)
      }
    },

    async handleBackButton() {
      this.showIllustration = false
      if (this.mode === 'connect-clavo-to-integration' || this.mode === 'integration-not-in-clavo' || this.mode === 'clavo-not-in-integration') {
        this.isFirstLoad = false
        this.members = []
        this.mode = 'clavo-not-in-integration'
        await this.loadIntegrations()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
section {
  margin: 35px;
}

@include respond-below(sm) {
  section {
    margin: 20px;
  }
}
</style>
